import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { SelectedItemChips } from '~/chords/MultiSelect/components/SelectedItemChips';

export interface RenderDropdownProps<T> {
  onSelect: (item: T) => void;
  selectedValues: ReadonlySet<string>;
  resetKey: number;
  disabled: boolean;
}

export interface MultiSelectProps<T> {
  value: T[];
  onValueChange: (items: T[]) => void;
  getItemLabel: (item: T) => string;
  getItemValue: (item: T) => string;
  renderDropdown: (props: RenderDropdownProps<T>) => ReactNode;
  disabled?: boolean;
}

export function MultiSelect<T>({
  value,
  onValueChange,
  getItemLabel,
  getItemValue,
  renderDropdown,
  disabled = false,
}: MultiSelectProps<T>) {
  const [resetKey, setResetKey] = useState(0);

  const selectedValues = useMemo(
    () => new Set(value.map((item) => getItemValue(item))),
    [value, getItemValue],
  );

  const handleSelect = (item: T) => {
    if (disabled) {
      return;
    }

    const itemValue = getItemValue(item);

    if (selectedValues.has(itemValue)) {
      return;
    }

    onValueChange([...value, item]);
    setResetKey((prev) => prev + 1);
  };

  const handleRemove = (itemValue: string) => {
    if (disabled) {
      return;
    }

    onValueChange(value.filter((item) => getItemValue(item) !== itemValue));
  };

  const chipItems = value.map((item) => ({
    value: getItemValue(item),
    label: getItemLabel(item),
  }));

  return (
    <div className="flex flex-col gap-sm">
      <SelectedItemChips
        items={chipItems}
        onRemove={handleRemove}
        disabled={disabled}
      />
      {renderDropdown({
        onSelect: handleSelect,
        selectedValues,
        resetKey,
        disabled,
      })}
    </div>
  );
}

MultiSelect.displayName = 'MultiSelect';
